import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isVerifying = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const verifyUserAccess = async (accessToken?: string) => {
    if (isVerifying.current) return;
    isVerifying.current = true;

    try {
      let token = accessToken;
      if (!token) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setIsCheckingAuth(false);
          isVerifying.current = false;
          return;
        }
        token = session.access_token;
      }

      const { data, error } = await supabase.functions.invoke('verify-user-access', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (error) {
        toast({ title: "Couldn't verify access. Please try again.", variant: "destructive" });
        await supabase.auth.signOut();
        setIsCheckingAuth(false);
        isVerifying.current = false;
        return;
      }

      if (!data?.authorized) {
        toast({ title: data?.message || "You are not authorised to access SPIRE. Contact your administrator.", variant: "destructive" });
        await supabase.auth.signOut();
        setIsCheckingAuth(false);
        isVerifying.current = false;
        return;
      }

      // Route based on user type. Client-type sessions must NOT land on
      // /clients/:id — that route (and all of /clients/*) is gated behind
      // StaffRoute (App.tsx), which redirects non-staff sessions straight
      // back to the portal login, producing a bounce/redirect loop
      // (CIR-2026-416). Client users share the same Supabase auth session
      // with the Client Portal (ClientPortalProvider derives its session
      // from the same supabase.auth session on mount), so we can route
      // straight to /client-portal/guided — the canonical client-login
      // landing page for every client user, either service tier
      // (CIR-2026-546) — without an extra hop through /client-login.
      if (data.userType === 'client' && data.clientOrganisationId) {
        navigate("/client-portal/guided", { replace: true });
      } else {
        // Staff (securapath) sessions continue to the staff client console.
        navigate("/clients", { replace: true });
      }
    } catch {
      toast({ title: "An unexpected error occurred. Please try again.", variant: "destructive" });
      await supabase.auth.signOut();
      setIsCheckingAuth(false);
      isVerifying.current = false;
    }
  };

  useEffect(() => {
    // Check for existing session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        verifyUserAccess();
      } else {
        setIsCheckingAuth(false);
      }
    });

    // Listen for auth state changes (handles OAuth callback)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            verifyUserAccess(session.access_token);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setIsCheckingAuth(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Please enter your email and password.", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: "Invalid email or password.", variant: "destructive" });
      setIsLoading(false);
    }
    // onAuthStateChange will handle the rest
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin + window.location.pathname,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });

    if (error) {
      toast({ title: "Couldn't sign in. Please try again.", variant: "destructive" });
      setIsGoogleLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your session...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm px-6">
        <div className="text-center space-y-2 mb-10">
          <h1 className="text-4xl font-bold tracking-tight text-primary">
            SPIRE
          </h1>
          <p className="text-sm text-muted-foreground">
            SecuraPath Intelligence Risk Engine
          </p>
        </div>

        {/* Client email/password login */}
        <form onSubmit={handleEmailSignIn} className="space-y-4 mb-6" data-testid="login-form">
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoComplete="email"
            data-testid="email-input"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            autoComplete="current-password"
            data-testid="password-input"
          />
          <Button type="submit" disabled={isLoading} className="w-full h-11" data-testid="sign-in-button">
            {isLoading ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              SecuraPath staff
            </span>
          </div>
        </div>

        {/* Google SSO for SecuraPath */}
        <Button
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
          className="w-full h-11 gap-3"
          variant="outline"
          data-testid="google-sign-in-button"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {isGoogleLoading ? "Signing in..." : "Sign in with Google"}
        </Button>
      </div>
    </div>
  );
};

export default Login;
